/**
 * Centralized GraphQL queries + mutations.
 *
 * Phase 2 ships a hand-curated set covering the Builder's CRUD needs.
 * Phase 5 should switch to graphql-codegen + generated TypedDocumentNodes.
 */

/* ----------------------------------- Reads -------------------------------- */

export const Q_LIST_FRONTEND_APPS = /* GraphQL */ `
  query ListFrontendApps($orgId: uuid!) {
    FrontendApp(where: { org_id: { _eq: $orgId } }, order_by: { updated_at: desc }) {
      id name type theme_id main_view_id created_at updated_at
    }
  }
`;

export const Q_GET_FRONTEND_APP = /* GraphQL */ `
  query GetFrontendApp($id: uuid!) {
    FrontendApp_by_pk(id: $id) {
      id org_id name type theme_id main_view_id
      bundle_identifier android_package deep_link_scheme
      icon_asset_url splash_asset_url
      theme { id name primary_color accent_color text_color background_color font_family }
      app_instances(order_by: { key: asc }) { id key value description }
    }
  }
`;

/**
 * Lean query for the App Settings "Navigation Structure" section.
 * Fetches only the columns needed to draw the navigator skeleton —
 * deliberately skips viewStyle / viewSource / interactions to keep
 * the App Settings page snappy.
 */
export const Q_LIST_NAV_VIEWS = /* GraphQL */ `
  query ListNavViews($appId: uuid!) {
    View(where: { frontend_app: { _eq: $appId } }) {
      id Type name sort
      child_views(order_by: { sort: asc }) { id View_id related_view_id sort }
    }
  }
`;

export const M_UPDATE_FRONTEND_APP_SETTINGS = /* GraphQL */ `
  mutation UpdateFrontendAppSettings(
    $id: uuid!
    $name: String!
    $bundle_identifier: String
    $android_package: String
    $deep_link_scheme: String
    $icon_asset_url: String
    $splash_asset_url: String
  ) {
    update_FrontendApp_by_pk(
      pk_columns: { id: $id }
      _set: {
        name: $name
        bundle_identifier: $bundle_identifier
        android_package: $android_package
        deep_link_scheme: $deep_link_scheme
        icon_asset_url: $icon_asset_url
        splash_asset_url: $splash_asset_url
      }
    ) {
      id name bundle_identifier android_package deep_link_scheme
      icon_asset_url splash_asset_url
    }
  }
`;

export const Q_FETCH_ALL_APP_DATA = /* GraphQL */ `
  query FetchAllAppData($appId: uuid!) {
    FrontendApp_by_pk(id: $appId) {
      id org_id name main_view_id
      theme { id primary_color accent_color text_color background_color font_family }
      app_instances { key value }
    }
    View(where: { frontend_app: { _eq: $appId } }) {
      id Type name sort mask_id
      viewStyle { id properties }
      viewSource { id type value url method query variables headers refresh_interval_seconds }
      child_views(order_by: { sort: asc }) { id View_id related_view_id sort }
      interactions(order_by: { created_at: asc }) {
        id view_id interaction_id
        interaction {
          id type name status
          Interaction_Actions(order_by: { priority: asc }) {
            id interaction_id action_id priority
            Action { id Type Value }
          }
        }
      }
    }
    InputMask { id name pattern placeholder }
  }
`;

export const Q_LIST_CORE_COMPONENTS = /* GraphQL */ `
  query ListCoreComponents {
    CoreComponent(order_by: { display_name: asc }) {
      id type display_name palette_group
      DynamicPropertyDefinitions(order_by: { priority: asc }) {
        id key type display_name priority value options
        SubCategory {
          id name priority
          Category { id name }
        }
      }
    }
  }
`;

export const Q_LIST_PACKAGES = /* GraphQL */ `
  query ListPackages($appId: uuid!) {
    app_package(where: { app_id: { _eq: $appId } }, order_by: { created_at: desc }, limit: 50) {
      id version package_name package_status created_at
    }
  }
`;

export const Q_RECENT_RELEASE_BUILDS = /* GraphQL */ `
  query RecentReleaseBuilds($appId: uuid!) {
    app_release_build(
      where: { app_release: { app_id: { _eq: $appId } } }
      order_by: { created_at: desc }
      limit: 25
    ) {
      id build_type status artifact_url store_submission_id error_message
      created_at updated_at
      app_release { id name env_type }
    }
  }
`;

/* ----------------------------- Assets (CMS) -------------------------------
 * `Asset` is org-scoped — the builder lists everything in the caller's org
 * regardless of which app is open. Permissions on the table enforce that.
 * Filtering by `kind` lets the icon picker show only icons, the splash
 * picker only splash images, and the library view show everything.
 * ------------------------------------------------------------------------- */

export const Q_LIST_ASSETS = /* GraphQL */ `
  query ListAssets($kinds: [String!]) {
    Asset(
      where: { kind: { _in: $kinds } }
      order_by: { created_at: desc }
    ) {
      id kind name mime_type storage_url file_size created_at
    }
  }
`;

export const Q_LIST_ALL_ASSETS = /* GraphQL */ `
  query ListAllAssets {
    Asset(order_by: { created_at: desc }) {
      id kind name mime_type storage_url file_size created_at
    }
  }
`;

export const M_DELETE_ASSET = /* GraphQL */ `
  mutation DeleteAsset($id: uuid!) {
    delete_Asset_by_pk(id: $id) { id }
  }
`;

/* ---------------------------- Structural mutations ------------------------ */

export const M_CREATE_FRONTEND_APP = /* GraphQL */ `
  mutation CreateFrontendApp($name: String!, $type: String!) {
    insert_FrontendApp_one(object: { name: $name, type: $type }) {
      id name
    }
  }
`;

export const M_INSERT_VIEW = /* GraphQL */ `
  mutation InsertView($appId: uuid!, $type: String!, $name: String!, $sort: Int!) {
    insert_View_one(object: {
      frontend_app: $appId, Type: $type, name: $name, sort: $sort
    }) { id Type name sort }
  }
`;

export const M_INSERT_VIEW_VIEW = /* GraphQL */ `
  mutation InsertViewView($parentId: uuid!, $childId: uuid!, $sort: Int!) {
    insert_View_View_one(
      object: { View_id: $parentId, related_view_id: $childId, sort: $sort }
      on_conflict: { constraint: View_View_View_id_related_view_id_key, update_columns: [sort] }
    ) { id }
  }
`;

/**
 * Sets `FrontendApp.main_view_id` — the single tree root the engine renders.
 * Used by Canvas.tsx (auto-promote first dropped view) and by the App
 * Settings navigation templates (single-screen / stack / tab-3).
 */
export const M_SET_MAIN_VIEW = /* GraphQL */ `
  mutation SetMainView($appId: uuid!, $viewId: uuid!) {
    update_FrontendApp_by_pk(
      pk_columns: { id: $appId }
      _set: { main_view_id: $viewId }
    ) { id main_view_id }
  }
`;

export const M_REORDER_CHILDREN = /* GraphQL */ `
  mutation ReorderChildren($edges: [View_View_insert_input!]!) {
    insert_View_View(
      objects: $edges
      on_conflict: { constraint: View_View_pkey, update_columns: [sort] }
    ) { affected_rows }
  }
`;

export const M_REPARENT_CHILD = /* GraphQL */ `
  mutation ReparentChild($oldEdgeId: uuid!, $newParentId: uuid!, $childId: uuid!, $sort: Int!) {
    delete_View_View_by_pk(id: $oldEdgeId) { id }
    insert_View_View_one(object: {
      View_id: $newParentId, related_view_id: $childId, sort: $sort
    }) { id }
  }
`;

export const M_DELETE_VIEW_HIERARCHY = /* GraphQL */ `
  mutation DeleteViewHierarchy($viewId: uuid!) {
    delete_view_hierarchy(args: { view_id: $viewId }) {
      id
      Type
      name
    }
  }
`;

/**
 * Swap two View_View edge sorts atomically. Used by ViewTree's
 * Move Up / Move Down actions — the two affected siblings exchange
 * their `sort` column.
 */
export const M_SWAP_CHILD_SORTS = /* GraphQL */ `
  mutation SwapChildSorts($aId: uuid!, $aSort: Int!, $bId: uuid!, $bSort: Int!) {
    a: update_View_View_by_pk(pk_columns: { id: $aId }, _set: { sort: $aSort }) {
      id sort
    }
    b: update_View_View_by_pk(pk_columns: { id: $bId }, _set: { sort: $bSort }) {
      id sort
    }
  }
`;

/**
 * Deep-clone a view and all descendants (Postgres function — atomic).
 * The clone is inserted as the next sibling of the source under the
 * source's original parent.
 */
export const M_DUPLICATE_VIEW_HIERARCHY = /* GraphQL */ `
  mutation DuplicateViewHierarchy($viewId: uuid!) {
    duplicate_view_hierarchy(args: { source_view_id: $viewId }) {
      id Type name
    }
  }
`;

/* ------------------------------- Save mutation ---------------------------- */
/**
 * Updates the View row's top-level fields. Caller passes only the columns
 * being changed in `$set` — keeps NOT NULL columns safe and avoids
 * touching unrelated fields.
 *
 * viewStyle / viewSource are upserted via separate mutations BEFORE this
 * one (see save-pipeline/savePipeline.ts) and linked via M_LINK_VIEW_STYLE
 * / M_LINK_VIEW_SOURCE. childViews are managed by the structural mutations
 * (M_INSERT_VIEW_VIEW, M_REORDER_CHILDREN, M_REPARENT_CHILD).
 */
export const M_UPSERT_VIEW = /* GraphQL */ `
  mutation UpsertView($id: uuid!, $set: View_set_input!) {
    update_View_by_pk(pk_columns: { id: $id }, _set: $set) {
      id name Type
      viewStyle { id properties }
      viewSource { id type value }
    }
  }
`;

export const M_UPSERT_VIEW_STYLE = /* GraphQL */ `
  mutation UpsertViewStyle($id: uuid!, $properties: jsonb!) {
    update_ViewStyle_by_pk(pk_columns: { id: $id }, _set: { properties: $properties }) {
      id properties
    }
  }
`;

export const M_INSERT_VIEW_STYLE = /* GraphQL */ `
  mutation InsertViewStyle($properties: jsonb!) {
    insert_ViewStyle_one(object: { properties: $properties }) { id properties }
  }
`;

export const M_UPSERT_VIEW_SOURCE = /* GraphQL */ `
  mutation UpsertViewSource(
    $id: uuid!, $type: String!, $value: String, $url: String,
    $method: String, $query: String, $variables: jsonb
  ) {
    update_ViewSource_by_pk(
      pk_columns: { id: $id }
      _set: { type: $type, value: $value, url: $url, method: $method, query: $query, variables: $variables }
    ) { id type value }
  }
`;

export const M_INSERT_VIEW_SOURCE = /* GraphQL */ `
  mutation InsertViewSource(
    $type: String!, $value: String, $url: String,
    $method: String, $query: String, $variables: jsonb
  ) {
    insert_ViewSource_one(object: {
      type: $type, value: $value, url: $url, method: $method, query: $query, variables: $variables
    }) { id type value }
  }
`;

export const M_LINK_VIEW_STYLE = /* GraphQL */ `
  mutation LinkViewStyle($viewId: uuid!, $styleId: uuid!) {
    update_View_by_pk(pk_columns: { id: $viewId }, _set: { view_style_id: $styleId }) { id }
  }
`;

export const M_LINK_VIEW_SOURCE = /* GraphQL */ `
  mutation LinkViewSource($viewId: uuid!, $sourceId: uuid!) {
    update_View_by_pk(pk_columns: { id: $viewId }, _set: { view_source_id: $sourceId }) { id }
  }
`;

/* ---------------------------- Interactions (Phase 5) ---------------------- */

export const Q_VIEW_INTERACTIONS = /* GraphQL */ `
  query ViewInteractions($viewId: uuid!) {
    View_Interaction(where: { view_id: { _eq: $viewId } }, order_by: { created_at: asc }) {
      id view_id interaction_id
      interaction {
        id type name status
        Interaction_Actions(order_by: { priority: asc }) {
          id interaction_id action_id priority
          Action { id Type Value }
        }
      }
    }
  }
`;

/**
 * Create a fresh interaction for a view in one nested insert. Inserts:
 *   View_Interaction → Interaction → Interaction_Action → Action
 * The Action starts with empty Type/Value; the user picks them in the UI.
 */
export const M_CREATE_VIEW_INTERACTION = /* GraphQL */ `
  mutation CreateViewInteraction($viewId: uuid!, $type: String!) {
    insert_View_Interaction_one(object: {
      view_id: $viewId,
      interaction: { data: {
        type: $type,
        Interaction_Actions: { data: {
          Action: { data: { Type: "Open Link", Value: "" } },
          priority: 0
        } }
      } }
    }) {
      id view_id interaction_id
      interaction {
        id type name status
        Interaction_Actions(order_by: { priority: asc }) {
          id interaction_id action_id priority
          Action { id Type Value }
        }
      }
    }
  }
`;

export const M_UPDATE_INTERACTION_TYPE = /* GraphQL */ `
  mutation UpdateInteractionType($id: uuid!, $type: String!) {
    update_Interaction_by_pk(pk_columns: { id: $id }, _set: { type: $type }) { id type }
  }
`;

export const M_DELETE_VIEW_INTERACTION = /* GraphQL */ `
  mutation DeleteViewInteraction($viewInteractionId: uuid!, $interactionId: uuid!) {
    delete_View_Interaction_by_pk(id: $viewInteractionId) { id }
    delete_Interaction_by_pk(id: $interactionId) { id }
  }
`;

/**
 * Add a new action to an existing interaction. Mirrors the create flow
 * above but for a single Action + Interaction_Action pair.
 */
export const M_ADD_ACTION = /* GraphQL */ `
  mutation AddAction($interactionId: uuid!, $priority: Int!, $type: String!) {
    insert_Interaction_Action_one(object: {
      interaction_id: $interactionId,
      priority: $priority,
      Action: { data: { Type: $type, Value: "" } }
    }) {
      id interaction_id action_id priority
      Action { id Type Value }
    }
  }
`;

export const M_UPDATE_ACTION = /* GraphQL */ `
  mutation UpdateAction($id: uuid!, $type: String!, $value: String) {
    update_Action_by_pk(pk_columns: { id: $id }, _set: { Type: $type, Value: $value }) {
      id Type Value
    }
  }
`;

export const M_UPDATE_ACTION_PRIORITY = /* GraphQL */ `
  mutation UpdateActionPriority($id: uuid!, $priority: Int!) {
    update_Interaction_Action_by_pk(pk_columns: { id: $id }, _set: { priority: $priority }) {
      id priority
    }
  }
`;

export const M_DELETE_ACTION = /* GraphQL */ `
  mutation DeleteAction($interactionActionId: uuid!, $actionId: uuid!) {
    delete_Interaction_Action_by_pk(id: $interactionActionId) { id }
    delete_Action_by_pk(id: $actionId) { id }
  }
`;

/* -------------------------- Packaging + publishing ------------------------ */

export const M_INSERT_APP_PACKAGE = /* GraphQL */ `
  mutation InsertAppPackage(
    $appId: uuid!, $version: String!, $packageName: String,
    $platformVersion: String, $packageData: jsonb!
  ) {
    insert_app_package_one(object: {
      app_id: $appId, version: $version, package_name: $packageName,
      platform_version: $platformVersion, package_data: $packageData
    }) { id version created_at }
  }
`;
